import React from 'react';
import {
  StyledWrapper,
  StyledColumn,
  StyledSectionTitle,
  StyledItem,
  StyledDashedLine,
  StyledValue,
} from './PropertyInfoStyles';
import { IProperty } from '@/models/property.model';
import { useTranslation } from 'react-i18next';
import { HouseIcon, OwnerIcon } from '@/icons';
import { renderPercentage } from '@/utils';

type TPropertyInformationProps = {
  property: IProperty;
};

const PropertyInformation: React.FC<TPropertyInformationProps> = ({ property }) => {
  const { t } = useTranslation(['property']);

  return (
    <StyledWrapper>
      <StyledColumn>
        <StyledSectionTitle>
          <HouseIcon /> {t('property_information_title')}
        </StyledSectionTitle>
        <StyledItem>
          <label>{t('property_gnr_label')}</label>
          <StyledDashedLine />
          <StyledValue>{property.gnr ?? '-'}</StyledValue>
        </StyledItem>

        <StyledItem>
          <label>{t('property_bnr_label')}</label>
          <StyledDashedLine />
          <StyledValue>{property.bnr ?? '-'}</StyledValue>
        </StyledItem>

        {property.snr && property.snr !== '0' && (
          <StyledItem>
            <label>{t('property_seksjonsnr_label')}</label>
            <StyledDashedLine />
            <StyledValue>{property.snr}</StyledValue>
          </StyledItem>
        )}

        {property.housing_association_share_id && property.housing_association_share_id !== '0' && (
          <StyledItem>
            <label>{t('property_housing_association_share_id_label')}</label>
            <StyledDashedLine />
            <StyledValue>{property.housing_association_share_id}</StyledValue>
          </StyledItem>
        )}

        <StyledItem>
          <label>{t('property_boligtype_label')}</label>
          <StyledDashedLine />
          <StyledValue>
            {t('property_type_details', {
              context: property.property_type,
            })}
          </StyledValue>
        </StyledItem>

        <StyledItem>
          <label>{t('property_soverom_label')}</label>
          <StyledDashedLine />
          <StyledValue>{property.bedrooms ?? '-'}</StyledValue>
        </StyledItem>

        <StyledItem>
          <label>{t('property_bruksareal_label')}</label>
          <StyledDashedLine />
          <StyledValue>{property.bra ?? '-'}</StyledValue>
        </StyledItem>

        <StyledItem>
          <label>{t('property_byggeaar_label')}</label>
          <StyledDashedLine />
          <StyledValue>{property.estimated_year_built ?? '-'}</StyledValue>
        </StyledItem>
      </StyledColumn>

      <StyledColumn>
        <StyledSectionTitle>
          <OwnerIcon /> {t('property_owners_title')}
        </StyledSectionTitle>
        {property?.owners?.map((owner) => (
          <StyledItem key={owner.id}>
            {owner?.full_name}
            <StyledDashedLine />
            <strong>{renderPercentage(owner?.ownership_share?.numerator / owner?.ownership_share?.denominator)}</strong>
          </StyledItem>
        ))}
      </StyledColumn>

      <StyledColumn>
        <StyledItem>
          <label>{t('property_other_small_houses_label')}</label>
          <StyledDashedLine />
          <StyledValue>
            {t(
              property.requires_extra_outdoor_appraisal
                ? 'property_requires_extra_outdoor_appraisal_label'
                : 'property_not_requires_extra_outdoor_appraisal_label',
            )}
          </StyledValue>
        </StyledItem>
      </StyledColumn>
    </StyledWrapper>
  );
};

export default PropertyInformation;
